import { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Divider,
  Avatar,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  PersonAdd,
  LocalHospital,
  PhotoCamera,
  Delete,
  CheckCircle,
  Save
} from '@mui/icons-material';
import axios from 'axios';
import CameraCapture from './CameraCapture';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function PublicPatientRegistration() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'male',
    phone: '',
    email: '',
    address: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    aadharNumber: '',
    fatherHusbandName: '',
    photoBlob: '',
    photoMimeType: ''
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [registeredPatient, setRegisteredPatient] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCameraCapture = (imageData, mimeType) => {
    setFormData({
      ...formData,
      photoBlob: imageData,
      photoMimeType: mimeType
    });
    setPhotoPreview(imageData);
    setCameraOpen(false);
  };

  const handleRemovePhoto = () => {
    setFormData({
      ...formData,
      photoBlob: '',
      photoMimeType: ''
    });
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.dateOfBirth ||
          !formData.gender || !formData.phone) {
        throw new Error('Please fill in all required fields');
      }

      // Validate phone number
      if (!/^\d{10}$/.test(formData.phone)) {
        throw new Error('Please enter a valid 10-digit phone number');
      }

      // Validate email if provided
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Submit registration
      const response = await axios.post(`${API_URL}/patients/register`, formData);

      setRegisteredPatient(response.data.data);
      setSuccess('Registration successful!');

      // Clear form
      setFormData({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        gender: 'male',
        phone: '',
        email: '',
        address: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        aadharNumber: '',
        fatherHusbandName: '',
        photoBlob: '',
        photoMimeType: ''
      });
      setPhotoPreview(null);

    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewRegistration = () => {
    setRegisteredPatient(null);
    setSuccess('');
    setError('');
  };

  if (registeredPatient) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Card elevation={8}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom fontWeight="bold" color="success.main">
                Registration Successful!
              </Typography>
              <Divider sx={{ my: 3 }} />

              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  Patient Details:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Patient Code</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary">
                        {registeredPatient.patientCode}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {registeredPatient.firstName} {registeredPatient.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">
                        {registeredPatient.phone}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Please save your Patient Code: <strong>{registeredPatient.patientCode}</strong>
                  <br />
                  You will need this code for all future visits and consultations.
                </Typography>
              </Alert>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleNewRegistration}
              >
                Register Another Patient
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Paper elevation={8} sx={{ mb: 3, p: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <LocalHospital sx={{ fontSize: 48, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                Naturopathy Hospital
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Patient Registration
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Registration Form */}
        <Card elevation={8}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PersonAdd sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Typography variant="h5" component="h2" fontWeight="bold">
                New Patient Registration
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Snackbar
              open={!!success}
              autoHideDuration={6000}
              onClose={() => setSuccess('')}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
                {success}
              </Alert>
            </Snackbar>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Date of Birth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    select
                    label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    inputProps={{ maxLength: 10 }}
                    helperText="10 digit mobile number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Emergency Contact Name"
                    name="emergencyContactName"
                    value={formData.emergencyContactName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Emergency Contact Phone"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={handleChange}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Father/Husband Name"
                    name="fatherHusbandName"
                    value={formData.fatherHusbandName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Aadhar Number"
                    name="aadharNumber"
                    value={formData.aadharNumber}
                    onChange={handleChange}
                    inputProps={{ maxLength: 12 }}
                    helperText="12-digit Aadhar number"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Patient Photo (Optional)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      {photoPreview ? (
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            src={photoPreview}
                            sx={{ width: 120, height: 120 }}
                            variant="rounded"
                          />
                          <IconButton
                            onClick={handleRemovePhoto}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Avatar
                          sx={{ width: 120, height: 120, bgcolor: 'grey.200' }}
                          variant="rounded"
                        >
                          <PersonAdd sx={{ fontSize: 60, color: 'grey.400' }} />
                        </Avatar>
                      )}
                      <Button
                        variant="outlined"
                        startIcon={<PhotoCamera />}
                        onClick={() => setCameraOpen(true)}
                      >
                        {photoPreview ? 'Retake Photo' : 'Take Photo'}
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    startIcon={<Save />}
                  >
                    {loading ? 'Registering...' : 'Register Patient'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <Paper elevation={2} sx={{ mt: 3, p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
          <Typography variant="caption" color="text.secondary">
            All information provided will be kept confidential and used only for medical purposes.
          </Typography>
        </Paper>

        <CameraCapture
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={handleCameraCapture}
        />
      </Container>
    </Box>
  );
}

export default PublicPatientRegistration;
